/**
 * «Всё видно» dataProvider: Atomic's supabase dataProvider (PostgREST through
 * ra-data-postgrest) with Supabase edge functions and storage replaced by
 * crm-api calls:
 *   salesCreate        → POST  /api/users
 *   salesUpdate        → PATCH /api/users/:id (+ POST /api/users/:id/disable)
 *   updatePassword     → POST  /api/auth/password (then signs in again)
 *   mergeContacts      → POST  /api/merge-contacts
 *   convertLead        → RPC   vv_convert_lead (PostgREST, as the user)
 *   placeCall          → POST  /api/telephony/call (the PBX rings the user, then the client)
 *   getSecrets / setSecret / generateSecret
 *                      → GET /api/settings/secrets, PUT /api/settings/secret/:key,
 *                        POST /api/settings/secret/:key/generate (Настройки → Подключения)
 *   setMyTelegramChatId / testTelegram
 *                      → PUT /api/me/telegram, POST /api/me/telegram/test (profile)
 *   saveOneC / testOneC
 *                      → PUT /api/settings/onec, POST /api/settings/onec/test (Подключения → 1С)
 *   saveBitrixMode / testBitrix / runBitrix / getBitrixStatus
 *                      → PUT /api/settings/bitrix, POST /api/settings/bitrix/test,
 *                        POST /api/settings/bitrix/run, GET /api/settings/bitrix/status
 *                        (Подключения → Битрикс24)
 *   getMailSettings / saveMailbox / clearMailbox / testMailbox
 *                      → GET /api/mail/settings, PUT|DELETE /api/mail/settings/:scope,
 *                        POST /api/mail/settings/:scope/test (profile «Почта», Подключения)
 *   renderMailTemplate / sendMail / uploadMailFile
 *                      → POST /api/mail/render, POST /api/mail/send, POST /api/files (letters)
 *   attachments/logos  → POST  /api/files
 *   signUp             → not available (the owner is created with the CLI)
 */
import { supabaseDataProvider } from "ra-supabase-core";
import {
  fetchUtils,
  HttpError,
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type GetManyReferenceParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SearchResourceName,
  SearchResult,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { searchDealCategories } from "../supabase/searchDealCategories";
import { keepConfigKeys } from "../commons/keepConfigKeys";
import { RESPONSIBLE_IN_PROJECT } from "../commons/responsibleFilter";
import { signIn } from "./authProvider";
import { API_KEY, apiFetch, getSupabaseClient, tokenStore } from "./client";

/** what Подключения → 1С saves */
export type OneCPatch = {
  enabled?: boolean;
  odataUrl?: string;
  dealKeyPrefix?: string;
  dealKeySuffix?: string;
};
/** what «Проверить подключение» answers when 1С replied */
export type OneCCheck = {
  ok: true;
  sample: { number: string | null; date: string | null } | null;
};

/** where the CRM data lives: here, or in the client's Bitrix24 (synced every 10 minutes) */
export type CrmSource = "own" | "bitrix";
export type BitrixCounts = {
  created: number;
  updated: number;
  skipped: number;
};
/** the report of a Bitrix24 run (crm-api connectors/types.ts) */
export type BitrixReport = {
  kind: "import" | "sync" | "dry";
  dry: boolean;
  startedAt: string;
  finishedAt: string;
  counts: Record<string, BitrixCounts>;
  archived: Record<string, number>;
  users: {
    matched: number;
    unmatched: { id: string; name: string; email: string | null }[];
  };
  lines: string[];
};
export type BitrixStatus = {
  crmSource: CrmSource;
  hasWebhook: boolean;
  /** what runs now: import | dry | sync | starting, or null */
  running: string | null;
  runStartedAt: string | null;
  lastPulledAt: string | null;
  lastReconciledAt: string | null;
  lastReport: BitrixReport | null;
  lastError: string | null;
};

/** which mailbox: the user's own, or the company's (the owner) */
export type MailScope = "me" | "company";
/** names of the filled mailbox fields, never their values */
export type MailSettings = {
  me: { keys: string[] };
  company: { keys: string[] };
  /** the owner only: employees with a personal mailbox */
  people?: { sales_id: number; keys: string[] }[];
};
export type MailCheck = {
  smtp: { ok: boolean; message: string };
  imap: { ok: boolean; message: string };
};
/** the card a letter belongs to: exactly one */
export type MailCard =
  { dealId: Identifier } | { leadId: Identifier } | { contactId: Identifier };
export type SendMailInput = {
  to: string[];
  cc: string[];
  subject: string;
  html: string;
  /** /api/files paths */
  attachments: string[];
} & MailCard;
export type UploadedMailFile = { path: string; name: string; size: number };

const HAS_CYRILLIC = /[А-Яа-яЁё]/;

/**
 * A database refusal in plain Russian. Our own triggers already speak
 * Russian and pass as they are; Postgres and PostgREST texts in English get
 * a short message by their code (SQLSTATE 42501 RLS / no privilege, 23514
 * check, PGRST* PostgREST). The status stays (401 still signs out).
 */
export const russianDbMessage = (
  code: unknown,
  message: string | undefined,
): string | undefined => {
  if (typeof code !== "string" || (message && HAS_CYRILLIC.test(message))) {
    return undefined;
  }
  if (code === "42501") return "Нет доступа к этой записи или действию";
  if (code === "23514")
    return "Значение не подходит: проверьте заполненные поля";
  if (code === "PGRST116") return "Запись не найдена или нет доступа";
  if (code.startsWith("PGRST")) return "Сервер не принял запрос";
  return undefined;
};

/** ra-data-postgrest transport: our token instead of a Supabase session. */
const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
  const headers = new Headers(options.headers);
  headers.set("apikey", API_KEY);
  const token = await tokenStore.get();
  try {
    return await fetchUtils.fetchJson(url, {
      ...options,
      headers,
      user: token
        ? { authenticated: true, token: `Bearer ${token}` }
        : undefined,
    });
  } catch (error) {
    if (error instanceof HttpError) {
      const ru = russianDbMessage(error.body?.code, error.message);
      if (ru) throw new HttpError(ru, error.status, error.body);
    }
    throw error;
  }
};

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: location.origin,
    apiKey: API_KEY,
    supabaseClient: getSupabaseClient(),
    httpClient,
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadFile(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const badRequest = (message: string) =>
  new HttpError(message, 400, { status: 400, message });

// The fields PATCH /api/users accepts. It rejects any other one.
const PATCHABLE = [
  "first_name",
  "last_name",
  "role",
  "department",
  "phone_ext",
  "telegram_chat_id",
  "can_delete",
  "can_export",
] as const;

type VvSale = Sale & {
  role?: "owner" | "head" | "manager";
  department?: string | null;
  phone_ext?: string | null;
  telegram_chat_id?: string | number | null;
  can_delete?: boolean;
  can_export?: boolean;
};

type SalesUpdateData = Partial<Omit<SalesFormData, "password">> &
  Partial<Pick<VvSale, (typeof PATCHABLE)[number]>>;

/** a list without the records gone in Bitrix24, unless the filter asks about them */
/** the resources whose records gone in Bitrix24 stay out of lists */
const GONE_HIDDEN = [
  "companies",
  "contacts",
  "leads",
  "companies_summary",
  "contacts_summary",
];

const withoutGone = <P extends { filter?: any }>(params: P): P => {
  const filter = params.filter ?? {};
  if (Object.keys(filter).some((k) => k.startsWith("ext_deleted_at"))) {
    return params;
  }
  return { ...params, filter: { ...filter, "ext_deleted_at@is": null } };
};

const responsibleCandidates = async (projectId: Identifier) => {
  const { data, error, status } = await getSupabaseClient().rpc(
    "vv_responsible_candidates",
    { p_project_id: Number(projectId) },
  );
  if (error) {
    throw new HttpError(
      russianDbMessage(error.code, error.message) ||
        error.message ||
        "Не получилось",
      status,
      error,
    );
  }
  const rows = (data ?? []) as Sale[];
  return { data: rows, total: rows.length };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      // records gone in Bitrix24 stay in the database (crm/db/18_external_ids.sql);
      // lists and pickers skip them, a link to one still opens it
      if (GONE_HIDDEN.includes(resource)) params = withoutGone(params);
      if (
        resource === "sales" &&
        params.filter?.[RESPONSIBLE_IN_PROJECT] != null
      ) {
        return responsibleCandidates(params.filter[RESPONSIBLE_IN_PROJECT]);
      }
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getManyReference(resource: string, params: GetManyReferenceParams) {
      // e.g. the contacts tab of a company: without the ones gone in Bitrix24
      if (GONE_HIDDEN.includes(resource)) params = withoutGone(params);
      return baseDataProvider.getManyReference(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp(_: SignUpData): Promise<{
      id: string;
      email: string;
      password: string;
    }> {
      throw badRequest(
        "Регистрация отключена: первого собственника создают командой create-owner",
      );
    },
    async salesCreate(body: SalesFormData): Promise<Sale> {
      const { email, password, first_name, last_name, administrator } = body;
      const extra = body as SalesFormData & Partial<VvSale>;
      const role = extra.role ?? (administrator ? "owner" : "manager");
      return apiFetch<Sale>("/api/users", {
        method: "POST",
        json: {
          email,
          password,
          first_name,
          last_name,
          role,
          ...(extra.department ? { department: extra.department } : {}),
        },
      });
    },
    async salesUpdate(id: Identifier, data: SalesUpdateData): Promise<Sale> {
      const { data: current } = await baseDataProvider.getOne<VvSale>("sales", {
        id,
      });

      if (data.email !== undefined && data.email !== current.email) {
        throw badRequest("Смену почты пока делать нельзя");
      }
      if (
        data.secondary_emails !== undefined &&
        JSON.stringify(data.secondary_emails ?? []) !==
          JSON.stringify(current.secondary_emails ?? [])
      ) {
        throw badRequest("Дополнительные почты пока менять нельзя");
      }
      const avatar = data.avatar as unknown as RAFile | string | undefined;
      const avatarSrc = typeof avatar === "string" ? avatar : avatar?.src;
      if (avatar !== undefined && avatarSrc !== current.avatar?.src) {
        throw badRequest("Фото пользователя пока менять нельзя");
      }

      const wanted: Record<string, unknown> = { ...data };
      if (
        data.administrator !== undefined &&
        Boolean(data.administrator) !== Boolean(current.administrator)
      ) {
        // Atomic's "administrator" switch was flipped: on = owner; off demotes
        // the owner to manager.
        wanted.role = data.administrator ? "owner" : "manager";
      }
      const patch: Record<string, unknown> = {};
      for (const field of PATCHABLE) {
        if (
          wanted[field] !== undefined &&
          wanted[field] !== (current as any)[field]
        ) {
          patch[field] = wanted[field];
        }
      }

      let result: Sale = current;
      if (Object.keys(patch).length > 0) {
        result = await apiFetch<Sale>(`/api/users/${id}`, {
          method: "PATCH",
          json: patch,
        });
      }
      if (
        data.disabled !== undefined &&
        Boolean(data.disabled) !== Boolean(current.disabled)
      ) {
        result = await apiFetch<Sale>(`/api/users/${id}/disable`, {
          method: "POST",
          json: { disabled: Boolean(data.disabled) },
        });
      }
      return result;
    },
    /**
     * Changes the current user's password. crm-api revokes every refresh
     * token on success, so the user is signed in again with the new password.
     */
    async updatePassword(
      _id: Identifier,
      passwords?: { current: string; next: string },
    ): Promise<true> {
      if (!passwords?.current || !passwords?.next) {
        throw badRequest("Укажите текущий и новый пароль");
      }
      const email = tokenStore.read()?.user.email;
      await apiFetch("/api/auth/password", {
        method: "POST",
        json: { current: passwords.current, next: passwords.next },
      });
      if (email) {
        await signIn(email, passwords.next);
      }
      return true;
    },
    async unarchiveDeal(deal: Deal) {
      // the deals of the same stage (stage_id: names repeat across pipelines)
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage_id: deal.stage_id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      const { data } = await getSupabaseClient()
        .from("init_state")
        .select("is_initialized");
      return data?.at(0)?.is_initialized > 0;
    },
    async globalSearch(
      query: string,
      resources: readonly SearchResourceName[],
      maxResults: number,
    ): Promise<SearchResult[]> {
      const { data, error, status } = await getSupabaseClient().rpc(
        "global_search",
        { query, resources, max_results: maxResults },
      );
      if (error) {
        console.error("global_search.error", error);
        // the status lets authProvider.checkError log out an expired session
        throw new HttpError("Failed to search", status, error);
      }
      return data ?? [];
    },
    /** «В сделку»: the deal made of the lead (the same one on a second call). */
    async convertLead(leadId: Identifier): Promise<{ dealId: Identifier }> {
      const { data, error, status } = await getSupabaseClient().rpc(
        "vv_convert_lead",
        { lead_id: Number(leadId) },
      );
      if (error) {
        throw new HttpError(
          russianDbMessage(error.code, error.message) ||
            error.message ||
            "Не получилось",
          status,
          error,
        );
      }
      return { dealId: data as Identifier };
    },
    /**
     * «Позвонить»: the PBX rings the current user's phone, then the client.
     * 501 «Звонок из CRM не настроен» when no PBX is set up.
     */
    async placeCall(phone: string): Promise<{ ok: true }> {
      return apiFetch("/api/telephony/call", {
        method: "POST",
        json: { phone },
      });
    },
    /** Подключения: the names of the filled secrets, never their values */
    async getSecrets(): Promise<string[]> {
      const { keys } = await apiFetch<{ keys: string[] }>(
        "/api/settings/secrets",
      );
      return keys;
    },
    /** writes a secret; an empty value deletes it */
    async setSecret(key: string, value: string): Promise<void> {
      await apiFetch(`/api/settings/secret/${encodeURIComponent(key)}`, {
        method: "PUT",
        json: { value },
      });
    },
    /** a new random key for an inbound hook, returned this once */
    async generateSecret(key: string): Promise<string> {
      const { value } = await apiFetch<{ value: string }>(
        `/api/settings/secret/${encodeURIComponent(key)}/generate`,
        { method: "POST" },
      );
      return value;
    },
    /** the current user's own Telegram chat_id ("" clears it) */
    async setMyTelegramChatId(
      chatId: string,
    ): Promise<{ telegram_chat_id: string | null }> {
      return apiFetch("/api/me/telegram", {
        method: "PUT",
        json: { chat_id: chatId },
      });
    },
    /** «Проверить»: a test message to the user's saved chat_id */
    async testTelegram(): Promise<{ ok: true }> {
      return apiFetch("/api/me/telegram/test", { method: "POST", json: {} });
    },
    /** Подключения → 1С: merged into configuration.config.onec, checked by crm-api */
    async saveOneC(
      patch: OneCPatch,
    ): Promise<{ onec: OneCPatch; passwordCleared: boolean }> {
      return apiFetch("/api/settings/onec", { method: "PUT", json: patch });
    },
    /** «Проверить подключение»: one request to 1С with the saved settings */
    async testOneC(): Promise<OneCCheck> {
      return apiFetch("/api/settings/onec/test", { method: "POST", json: {} });
    },
    /** Подключения → Битрикс24: own CRM or Bitrix24 as the source (owner) */
    async saveBitrixMode(
      crmSource: CrmSource,
    ): Promise<{ crmSource: CrmSource }> {
      return apiFetch("/api/settings/bitrix", {
        method: "PUT",
        json: { crmSource },
      });
    },
    /** «Проверить»: one `profile` call with the saved webhook */
    async testBitrix(): Promise<{ ok: true; name: string }> {
      return apiFetch("/api/settings/bitrix/test", {
        method: "POST",
        json: {},
      });
    },
    /** «Пробный перенос» (dry) or «Перенести» (import): runs in the background */
    async runBitrix(kind: "dry" | "import"): Promise<{ started: string }> {
      return apiFetch("/api/settings/bitrix/run", {
        method: "POST",
        json: { kind },
      });
    },
    /** the current run and the last report */
    async getBitrixStatus(): Promise<BitrixStatus> {
      return apiFetch("/api/settings/bitrix/status");
    },
    /** profile «Почта» and Подключения: which fields are filled */
    async getMailSettings(): Promise<MailSettings> {
      return apiFetch("/api/mail/settings");
    },
    /** sets the given fields of a mailbox; "" deletes one */
    async saveMailbox(
      scope: MailScope,
      fields: Record<string, string>,
    ): Promise<{ keys: string[] }> {
      return apiFetch(`/api/mail/settings/${scope}`, {
        method: "PUT",
        json: fields,
      });
    },
    /** deletes a whole mailbox: own, the company's or (the owner) an employee's */
    async clearMailbox(scope: MailScope | number): Promise<void> {
      await apiFetch(`/api/mail/settings/${scope}`, { method: "DELETE" });
    },
    /** «Проверить»: SMTP and IMAP login with the saved fields */
    async testMailbox(scope: MailScope): Promise<MailCheck> {
      return apiFetch(`/api/mail/settings/${scope}/test`, {
        method: "POST",
        json: {},
      });
    },
    /** a template with its {{…}} filled for this card */
    async renderMailTemplate(
      templateId: Identifier,
      card: MailCard,
    ): Promise<{ subject: string; html: string }> {
      return apiFetch("/api/mail/render", {
        method: "POST",
        json: { templateId, ...card },
      });
    },
    /** sends a letter; a refusal of the mail server is an error with the reason */
    async sendMail(
      input: SendMailInput,
    ): Promise<{ id: Identifier; status: "sent"; refused?: string[] }> {
      return apiFetch("/api/mail/send", { method: "POST", json: input });
    },
    /** a file for a letter, stored like note attachments */
    async uploadMailFile(file: File): Promise<UploadedMailFile> {
      const form = new FormData();
      form.append("file", file, file.name);
      const r = await apiFetch<UploadResponse>("/api/files", {
        method: "POST",
        body: form,
      });
      return { path: r.path, name: file.name, size: r.size ?? file.size };
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      return apiFetch("/api/merge-contacts", {
        method: "POST",
        json: { loserId: sourceId, winnerId: targetId },
      });
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadFile(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (incoming, dataProvider) => {
      const params = await keepConfigKeys(incoming, dataProvider);
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadFile(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadFile(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadFile(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params, dataProvider) => {
      return searchDealCategories(
        applyFullTextSearch(["name", "description"])(params),
        params.filter?.q,
        dataProvider,
      );
    },
  },
];

export const getDataProvider = () =>
  withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

type UploadResponse = {
  path: string;
  url: string;
  name: string;
  size: number;
  type: string;
};

/** Uploads a new file (blob:/data: src or rawFile) to crm-api, in place. */
const uploadFile = async (fi: RAFile) => {
  const isLocal =
    !fi.src || fi.src.startsWith("blob:") || fi.src.startsWith("data:");
  // already stored by crm-api, or an external link: keep it as is
  if (!isLocal) {
    return fi;
  }

  const dataContent: Blob | null = fi.src
    ? await fetch(fi.src)
        .then((res) => (res.status === 200 ? res.blob() : null))
        .catch(() => null)
    : (fi.rawFile ?? null);

  if (dataContent == null) {
    // We weren't able to read the file from its src: keep it as is.
    return fi;
  }

  const name =
    fi.rawFile?.name || fi.title || fi.src.split("/").pop() || "file";
  const form = new FormData();
  form.append("file", dataContent, name);
  const uploaded = await apiFetch<UploadResponse>("/api/files", {
    method: "POST",
    body: form,
  });

  fi.path = uploaded.path;
  fi.src = uploaded.url;
  fi.type = fi.rawFile?.type || uploaded.type;
  delete (fi as Partial<RAFile>).rawFile;

  return fi;
};
